// src/pages/MyClasses.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useMyClasses, ALL_YEAR_GROUPS, deriveKSName } from '@/hooks/useMyClasses';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';

export default function MyClasses() {
  const { user, profile, schoolPlan } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (schoolPlan !== null && schoolPlan !== 'individual') {
      navigate('/dashboard');
    }
  }, [schoolPlan, navigate]);
  const { yearGroups, classes, loading, error, refetch } = useMyClasses();

  // Add Class form
  const [addClassYGId, setAddClassYGId] = useState('');
  const [addClassName, setAddClassName] = useState('');
  const [addingClass, setAddingClass] = useState(false);

  // Add Year Group form
  const [addYGName, setAddYGName] = useState('');
  const [addingYG, setAddingYG] = useState(false);

  // Delete state
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // Year groups not yet in the school (for Add Year Group select)
  const existingYGNames = new Set(yearGroups.map((y) => y.name));
  const availableToAdd = ALL_YEAR_GROUPS.filter((yg) => !existingYGNames.has(yg));

  // Classes grouped by year group
  const grouped = yearGroups.map((yg) => ({
    yearGroup: yg,
    classes: classes.filter((c) => c.yearGroupId === yg.id).sort((a, b) => a.name.localeCompare(b.name)),
  }));

  const handleAddClass = async () => {
    const trimmed = addClassName.trim();
    if (!addClassYGId || !trimmed) return;

    // Duplicate check (case-insensitive within same year group)
    const exists = classes.some(
      (c) => c.yearGroupId === addClassYGId && c.name.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      toast({ title: 'A class with that name already exists in this year group.', variant: 'destructive' });
      return;
    }

    setAddingClass(true);
    let createdClassId: string | null = null;
    try {
      const { data: cls, error: clsError } = await supabase
        .from('classes')
        .insert({ name: trimmed, year_group_id: addClassYGId })
        .select('id')
        .single();
      if (clsError) throw new Error(clsError.message);
      createdClassId = cls.id;

      const { error: tcError } = await supabase
        .from('teacher_classes')
        .insert({ user_id: user!.id, class_id: cls.id });
      if (tcError) throw new Error(tcError.message);

      setAddClassName('');
      await refetch();
      toast({ title: 'Class added.' });
    } catch (err) {
      // If teacher_classes insert failed after class was created, clean up the orphaned class
      if (createdClassId) {
        await supabase.from('classes').delete().eq('id', createdClassId);
      }
      toast({ title: 'Failed to add class', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    } finally {
      setAddingClass(false);
    }
  };

  const handleAddYearGroup = async () => {
    if (!addYGName || !profile?.school_id) return;

    // Find the key_stage_id from existing yearGroups data
    const ksName = deriveKSName(addYGName);

    setAddingYG(true);
    try {
      // Find or create the key stage
      let keyStageId: string;
      const existingKS = yearGroups.find((yg) => yg.keyStageName === ksName);
      if (existingKS) {
        keyStageId = existingKS.keyStageId;
      } else {
        const { data: newKS, error: ksError } = await supabase
          .from('key_stages')
          .insert({ school_id: profile!.school_id, name: ksName })
          .select('id')
          .single();
        if (ksError) throw new Error(ksError.message);
        keyStageId = newKS.id;
      }

      const { error: ygError } = await supabase
        .from('year_groups')
        .insert({ key_stage_id: keyStageId, name: addYGName });
      if (ygError) throw new Error(ygError.message);

      setAddYGName('');
      await refetch();
      toast({ title: `${addYGName} added.` });
    } catch (err) {
      toast({ title: 'Failed to add year group', description: (err as Error).message, variant: 'destructive' });
    } finally {
      setAddingYG(false);
    }
  };

  const handleRenameClass = async (classId: string) => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    const { error } = await supabase
      .from('classes')
      .update({ name: trimmed })
      .eq('id', classId);
    if (error) {
      toast({ title: 'Failed to rename class', description: error.message, variant: 'destructive' });
    } else {
      setEditingId(null);
      setEditName('');
      await refetch();
      toast({ title: 'Class renamed.' });
    }
  };

  const confirmDelete = async (classId: string, className: string) => {
    const { count, error: countError } = await supabase
      .from('pupils')
      .select('*', { count: 'exact', head: true })
      .eq('class_id', classId);

    if (countError) {
      toast({ title: 'Could not check class pupils', description: countError.message, variant: 'destructive' });
      return;
    }
    if ((count ?? 0) > 0) {
      toast({
        title: 'Cannot delete class',
        description: 'This class has pupils and cannot be deleted. Archive all pupils first.',
        variant: 'destructive',
      });
      return;
    }
    setDeleteTarget({ id: classId, name: className });
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    const { id: classId, name: className } = deleteTarget;
    setDeleteTarget(null);
    setDeletingId(classId);
    try {
      await supabase.from('teacher_classes').delete().eq('class_id', classId).eq('user_id', user!.id);
      const { error: delError } = await supabase.from('classes').delete().eq('id', classId);
      if (delError) throw new Error(delError.message);
      await refetch();
      toast({ title: `"${className}" deleted.` });
    } catch (err) {
      toast({ title: 'Failed to delete class', description: err instanceof Error ? err.message : 'Unknown error', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <h1 className="text-3xl font-bold text-foreground">My Classes</h1>

        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading classes…
          </div>
        )}

        {error && <p className="text-destructive text-sm">{error}</p>}

        {!loading && !error && (
          <>
            {/* Class list grouped by year group */}
            {grouped.map(({ yearGroup, classes: yClasses }) => (
              <Card key={yearGroup.id} className="shadow-soft">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-muted-foreground">
                    {yearGroup.name}
                    <Badge variant="outline" className="ml-2 text-xs">{yearGroup.keyStageName}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {yClasses.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No classes yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {yClasses.map((cls) => (
                        <li key={cls.id} className="flex items-center justify-between py-1.5 border-b last:border-0 gap-2">
                          {editingId === cls.id ? (
                            <div className="flex items-center gap-2 flex-1">
                              <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="h-8 text-sm flex-1"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRenameClass(cls.id);
                                  if (e.key === 'Escape') { setEditingId(null); setEditName(''); }
                                }}
                              />
                              <Button size="sm" onClick={() => handleRenameClass(cls.id)} disabled={!editName.trim()}>
                                Save
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditName(''); }}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <>
                              <span className="text-sm font-medium flex-1">{cls.name}</span>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => { setEditingId(cls.id); setEditName(cls.name); }}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-destructive hover:text-destructive"
                                  disabled={deletingId === cls.id}
                                  onClick={() => confirmDelete(cls.id, cls.name)}
                                >
                                  {deletingId === cls.id
                                    ? <Loader2 className="h-4 w-4 animate-spin" />
                                    : <Trash2 className="h-4 w-4" />}
                                </Button>
                              </div>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            ))}

            {grouped.length === 0 && (
              <p className="text-muted-foreground text-sm">No year groups set up yet. Add one below.</p>
            )}

            {/* Add Class */}
            {yearGroups.length > 0 && (
              <Card className="shadow-soft">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Add a class</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1">
                    <Label>Year group</Label>
                    <Select value={addClassYGId} onValueChange={setAddClassYGId} disabled={addingClass}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select year group" />
                      </SelectTrigger>
                      <SelectContent>
                        {yearGroups.map((yg) => (
                          <SelectItem key={yg.id} value={yg.id}>{yg.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Class name</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g. Tuesday Group"
                        value={addClassName}
                        onChange={(e) => setAddClassName(e.target.value)}
                        disabled={addingClass}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddClass(); }}
                      />
                      <Button onClick={handleAddClass} disabled={!addClassYGId || !addClassName.trim() || addingClass}>
                        {addingClass ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Add Year Group */}
            {availableToAdd.length > 0 && (
              <Card className="shadow-soft">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Add a year group</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Select value={addYGName} onValueChange={setAddYGName} disabled={addingYG}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select year group to add" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableToAdd.map((yg) => (
                          <SelectItem key={yg} value={yg}>{yg}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAddYearGroup} disabled={!addYGName || addingYG}>
                      {addingYG ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete class?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deleteTarget?.name}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
